import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import {
  Settings, Menu, Plug, PanelLeftClose, PanelLeftOpen,
  Zap, MoreHorizontal, HelpCircle, X,
  PiggyBank, Megaphone, Boxes, Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import AICopilot from "./AICopilot";
import QuickActions from "./QuickActions";
import ProfitPulse from "./ProfitPulse";
import GlobalReconnectBanner from "./GlobalReconnectBanner";
import BetaFeedbackButton from "./BetaFeedbackButton";
import { safeStorage } from "../lib/safeStorage";

// Bordered Shield Logo (Option 5) — AI Growth Operator brand mark
function ShieldLogo({ size = "sm" }) {
  const dim = size === "md" ? 22 : 20;
  return (
    <div
      data-testid="brand-logo-shield"
      className="flex-shrink-0 inline-flex items-center justify-center rounded-md bg-white border-2 border-zinc-900"
      style={{ width: dim, height: dim }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={dim - 8}
        height={dim - 8}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-zinc-900"
        aria-hidden="true"
      >
        <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" />
      </svg>
    </div>
  );
}

// ── Aggressive simplification (v3 Command Centre) ──────────────────────
// Iter 178 nav-speed pass: promote the 4 highest-traffic destinations to
// the primary rail (Command Centre + Finance + Marketing + Inventory +
// Activation). Everything else stays under "More" so the sidebar stays
// scannable in <5 seconds. This directly targets the 2-minute rule.
const primaryNav = [
  { name: "Command Centre", href: "/",               icon: Zap        },
  { name: "Activate",       href: "/activate",       icon: Sparkles   },
  { name: "Finance",        href: "/finance",        icon: PiggyBank  },
  { name: "Marketing",      href: "/marketing",      icon: Megaphone  },
  { name: "Inventory",      href: "/inventory-aware", icon: Boxes     },
];

// "More →" — the deep-work areas. Grouped for scannability inside the popover.
// Legacy dashboards removed (iter 171). Only routes that are first-class in v3.
// Iter 178 nav-speed pass: Finance / Marketing / Inventory are now in the
// primary rail — the "More" popover only holds the deeper-work areas.
const moreNav = [
  {
    group: "Financials",
    items: [
      { name: "COGS Management",   href: "/cogs-management" },
      { name: "Report Center",     href: "/report-center" },
    ],
  },
  {
    group: "Growth",
    items: [
      { name: "Products",           href: "/products-view" },
      { name: "Pricing",            href: "/smart-pricing" },
      { name: "Buyers · Retention", href: "/retention" },
      { name: "Competitors",        href: "/competitor-analysis" },
    ],
  },
  {
    group: "Data & Automation",
    items: [
      { name: "Analytics",         href: "/analytics" },
      { name: "Attribution",       href: "/attribution" },
      { name: "Autopilot",         href: "/autopilot" },
      { name: "History",           href: "/history" },
      { name: "Results",           href: "/results" },
    ],
  },
];

const bottomNav = [
  { name: "Integrations", href: "/integrations-settings", icon: Plug },
  { name: "Settings",     href: "/settings",              icon: Settings },
  { name: "Help",         href: "/help",                  icon: HelpCircle },
];

const COLLAPSE_KEY = "sidebar_collapsed";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(COLLAPSE_KEY, false) === true);
  const location = useLocation();
  const morePopoverRef = useRef(null);

  useEffect(() => {
    safeStorage.set(COLLAPSE_KEY, collapsed);
  }, [collapsed]);

  // Close More popover on outside click / route change / Escape
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);
  useEffect(() => {
    function handleClick(e) {
      if (!morePopoverRef.current) return;
      if (!morePopoverRef.current.contains(e.target)) setMoreOpen(false);
    }
    function handleKey(e) { if (e.key === "Escape") setMoreOpen(false); }
    if (moreOpen) {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleKey);
      return () => {
        document.removeEventListener("mousedown", handleClick);
        document.removeEventListener("keydown", handleKey);
      };
    }
  }, [moreOpen]);

  const isCommandCentreActive =
    location.pathname === "/" || location.pathname === "/command-centre";

  return (
    <div className="flex min-h-screen bg-white text-aigo-dark">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-aigo-dark/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}

      <aside className={cn(
        "fixed lg:sticky lg:top-0 inset-y-0 lg:inset-y-auto left-0 z-50 h-screen lg:h-screen bg-white border-r border-zinc-200 transform transition-all duration-200 flex flex-col flex-shrink-0",
        collapsed ? "lg:w-16 w-56" : "w-56",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )} data-testid="sidebar" data-collapsed={collapsed ? "1" : "0"}>
        <div className={cn("pt-4 pb-3 flex items-center justify-between gap-2", collapsed ? "px-2.5" : "px-4")}>
          <div className="flex items-center gap-2 min-w-0" data-testid="brand-sidebar">
            <ShieldLogo size="sm" />
            {!collapsed && (
              <span className="font-heading font-bold text-aigo-dark text-[13px] tracking-tight truncate">AI Growth Operator</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="hidden lg:inline-flex w-6 h-6 items-center justify-center rounded text-zinc-400 hover:text-aigo-dark hover:bg-aigo-mint-soft flex-shrink-0"
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
          </button>
        </div>

        <nav className="flex-1 px-2 space-y-1 overflow-visible" data-testid="main-nav">
          {/* Primary rail — top-traffic destinations */}
          {primaryNav.map((item) => {
            const isRootItem = item.href === "/";
            const isActive = isRootItem
              ? isCommandCentreActive
              : (location.pathname === item.href || location.pathname.startsWith(item.href + "/"));
            return (
              <NavLink
                key={item.name}
                to={item.href}
                end={isRootItem}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
                title={collapsed ? item.name : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-md text-[12.5px] font-medium transition-all duration-150",
                  collapsed ? "justify-center px-1.5 py-2" : "px-2.5 py-1.5",
                  isActive
                    ? "bg-aigo-mint-soft text-aigo-dark border border-aigo-mint/40 shadow-[0_0_18px_rgba(193,219,194,0.25)]"
                    : "text-zinc-600 hover:bg-aigo-mint-soft hover:text-aigo-dark",
                )}
              >
                <item.icon className={cn("w-3.5 h-3.5 flex-shrink-0", isActive ? "text-aigo-dark" : "text-zinc-400")} />
                {!collapsed && item.name}
              </NavLink>
            );
          })}

          {/* More → popover trigger */}
          <div className="relative" ref={morePopoverRef}>
            <button
              type="button"
              data-testid="nav-more-trigger"
              onClick={() => setMoreOpen((o) => !o)}
              title={collapsed ? "More" : undefined}
              className={cn(
                "w-full flex items-center gap-2 rounded-md text-[12.5px] font-medium transition-all duration-150",
                collapsed ? "justify-center px-1.5 py-2" : "px-2.5 py-1.5",
                moreOpen
                  ? "bg-zinc-100 text-aigo-dark"
                  : "text-zinc-500 hover:bg-aigo-mint-soft hover:text-aigo-dark",
              )}
            >
              <MoreHorizontal className="w-3.5 h-3.5 flex-shrink-0 text-zinc-400" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">More</span>
                  <span className="text-[10px] text-zinc-400">{moreOpen ? "▾" : "▸"}</span>
                </>
              )}
            </button>

            {moreOpen && (
              <div
                data-testid="nav-more-popover"
                className={cn(
                  "absolute z-50 bg-white border border-zinc-200 rounded-lg shadow-lg p-1",
                  collapsed
                    ? "left-full ml-1 top-0 w-64"
                    : "left-full ml-1 top-0 w-64",
                )}
                role="menu"
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
                  <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-medium">All areas</span>
                  <button
                    type="button"
                    onClick={() => setMoreOpen(false)}
                    className="w-5 h-5 inline-flex items-center justify-center rounded text-zinc-400 hover:text-aigo-dark hover:bg-zinc-100"
                    aria-label="Close"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto py-1">
                  {moreNav.map((group) => (
                    <div key={group.group} className="py-1">
                      <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                        {group.group}
                      </div>
                      {group.items.map((item) => (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          data-testid={`nav-more-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => { setMoreOpen(false); setSidebarOpen(false); }}
                          className={({ isActive }) => cn(
                            "block px-3 py-1.5 text-[12.5px] rounded-sm transition-colors",
                            isActive
                              ? "bg-aigo-mint-soft text-aigo-dark font-medium"
                              : "text-zinc-600 hover:bg-aigo-mint-soft hover:text-aigo-dark",
                          )}
                        >
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="px-2 pb-1 space-y-px">
          {bottomNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.name.toLowerCase()}`}
              title={collapsed ? item.name : undefined}
              className={({ isActive }) => cn(
                "flex items-center gap-2 rounded-md text-[12.5px] transition-colors",
                collapsed ? "justify-center px-1.5 py-2" : "px-2.5 py-1.5",
                isActive
                  ? "bg-aigo-mint-soft text-aigo-dark border border-aigo-mint/40"
                  : "text-zinc-500 hover:bg-aigo-mint-soft hover:text-aigo-dark",
              )}
            >
              <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
              {!collapsed && item.name}
            </NavLink>
          ))}
        </div>

        <div className={cn("pt-1.5 pb-2 border-t border-zinc-200", collapsed ? "px-1.5 flex flex-col items-center gap-1" : "px-2 flex items-center justify-end gap-2")}>
          <p className={cn("text-[9px] text-zinc-400 leading-none whitespace-nowrap", collapsed && "rotate-90")}>v3.0</p>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 bg-white">
        <GlobalReconnectBanner />
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-zinc-200 px-4 py-3 lg:hidden">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn" className="text-aigo-dark hover:bg-aigo-mint-soft">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2" data-testid="brand-mobile">
              <ShieldLogo size="sm" />
              <span className="font-heading font-bold text-aigo-dark text-sm">AI Growth Operator</span>
            </div>
            <div className="w-10" />
          </div>
        </header>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>

      <AICopilot />
      <QuickActions />
      <ProfitPulse />
      <BetaFeedbackButton />
    </div>
  );
}
